<template>
  <div class="v2-preview-collections">
    <!-- Loading -->
    <div
      v-if="!isLoaded"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading..."
      />
    </div>

    <template v-else>
      <!-- Tabs -->
      <div class="v2-preview-collections__tabs">
        <button
          class="v2-preview-collections__tab"
          :class="{ 'v2-preview-collections__tab--active': activeTab === 'active' }"
          @click="activeTab = 'active'"
        >
          Active Collections
          <span class="v2-preview-collections__tab-count">{{ brandCollection.length }}</span>
        </button>
        <button
          class="v2-preview-collections__tab"
          :class="{ 'v2-preview-collections__tab--active': activeTab === 'previous' }"
          @click="activeTab = 'previous'"
        >
          Previous Collections
          <span class="v2-preview-collections__tab-count">{{ previousCollection.length }}</span>
        </button>
      </div>

      <!-- Active Collections Grid -->
      <div
        v-if="activeTab === 'active'"
        class="v2-preview-collections__grid"
      >
        <div
          v-for="collection in brandCollection"
          :key="collection._id"
          class="v2-preview-card"
          @click="onCollectionClick(collection)"
        >
          <div class="v2-preview-card__image">
            <img
              :src="collection.logo || defaultCollectionImage"
              :alt="collection.name"
              class="v2-preview-card__img"
              @error="onImageError"
            >
          </div>
          <div class="v2-preview-card__body">
            <div class="v2-preview-card__name">
              {{ collection.name }}
            </div>
            <div class="v2-preview-card__meta">
              {{ collection.productsCount || 0 }} Product(s)
            </div>
            <v2-badge
              v-if="collection.type"
              variant="default"
              class="v2-mt-1"
            >
              {{ getCollectionTypeText(collection.type) }}
            </v2-badge>
            <div
              v-if="collection.endDate"
              class="v2-preview-card__meta"
            >
              {{ parseCollectionDate(collection.endDate) }}
            </div>
            <div
              v-if="collection.updatedAt"
              class="v2-preview-card__meta"
            >
              Updated {{ formattedDate(collection.updatedAt) }}
            </div>
            <div
              v-if="collection.expectedDeliveryWindow && collection.expectedDeliveryWindow.fromDate && collection.expectedDeliveryWindow.toDate"
              class="v2-preview-card__meta"
            >
              Delivery window: {{ getMonthAndYearFromDate(collection.expectedDeliveryWindow.fromDate) }} - {{ getMonthAndYearFromDate(collection.expectedDeliveryWindow.toDate) }}
            </div>
            <div
              v-if="collection.description"
              class="v2-preview-card__description"
            >
              <span :class="{ 'v2-preview-card__description--truncated': !expandedDescriptions[collection._id] }">
                Description: {{ getDescription(collection) }}
              </span>
              <span
                v-if="collection.description.length > DESCRIPTION_MAX_LENGTH"
                class="v2-preview-card__read-more"
                @click.stop="toggleDescription(collection._id)"
              >
                Read {{ expandedDescriptions[collection._id] ? 'Less' : 'More' }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="!brandCollection.length"
          class="v2-preview-collections__empty"
        >
          <p class="v2-text-muted">
            No active collections
          </p>
        </div>
      </div>

      <!-- Previous Collections Grid -->
      <div
        v-if="activeTab === 'previous'"
        class="v2-preview-collections__grid"
      >
        <div
          v-for="collection in previousCollection"
          :key="collection._id"
          class="v2-preview-card"
          @click="onCollectionClick(collection)"
        >
          <div class="v2-preview-card__image">
            <img
              :src="collection.logo || defaultCollectionImage"
              :alt="collection.name"
              class="v2-preview-card__img"
              @error="onImageError"
            >
          </div>
          <div class="v2-preview-card__body">
            <div class="v2-preview-card__name">
              {{ collection.name }}
            </div>
            <div class="v2-preview-card__meta">
              {{ collection.productsCount || 0 }} Product(s)
            </div>
            <v2-badge
              variant="destructive"
              class="v2-mt-1"
            >
              Deadline Closed
            </v2-badge>
            <div
              v-if="collection.endDate"
              class="v2-preview-card__meta"
            >
              {{ parseCollectionDate(collection.endDate) }}
            </div>
            <div
              v-if="collection.updatedAt"
              class="v2-preview-card__meta"
            >
              Updated {{ formattedDate(collection.updatedAt) }}
            </div>
            <div
              v-if="collection.expectedDeliveryWindow && collection.expectedDeliveryWindow.fromDate && collection.expectedDeliveryWindow.toDate"
              class="v2-preview-card__meta"
            >
              Delivery window: {{ getMonthAndYearFromDate(collection.expectedDeliveryWindow.fromDate) }} - {{ getMonthAndYearFromDate(collection.expectedDeliveryWindow.toDate) }}
            </div>
            <div
              v-if="collection.description"
              class="v2-preview-card__description"
            >
              <span :class="{ 'v2-preview-card__description--truncated': !expandedDescriptions[collection._id] }">
                Description: {{ getDescription(collection) }}
              </span>
              <span
                v-if="collection.description.length > DESCRIPTION_MAX_LENGTH"
                class="v2-preview-card__read-more"
                @click.stop="toggleDescription(collection._id)"
              >
                Read {{ expandedDescriptions[collection._id] ? 'Less' : 'More' }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="!previousCollection.length"
          class="v2-preview-collections__empty"
        >
          <p class="v2-text-muted">
            No previous collections
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import { useCollectionList } from '@/composables/useCollectionList'
import { getDefaultProductImage, getCollectionTypeText, parseCollectionDate } from '@/@core/utils/utils'
import { utils } from '@kingpin-global/kingpin-utils-frontend'

const { formattedDate, getMonthAndYearFromDate } = utils
const DESCRIPTION_MAX_LENGTH = 50

export default {
  name: 'V2ProfilePreviewCollections',
  components: {
    V2Spinner,
    V2Badge,
  },
  setup() {
    const composable = useCollectionList()
    composable.fetchCollections()
    return { ...composable }
  },
  data() {
    return {
      activeTab: 'active',
      expandedDescriptions: {},
      defaultCollectionImage: getDefaultProductImage(300, 300),
      getCollectionTypeText,
      parseCollectionDate,
      getMonthAndYearFromDate,
      formattedDate,
      DESCRIPTION_MAX_LENGTH,
    }
  },
  methods: {
    onImageError(event) {
      event.target.src = this.defaultCollectionImage
    },
    getDescription(collection) {
      if (this.expandedDescriptions[collection._id]) {
        return collection.description
      }
      return collection.description.slice(0, DESCRIPTION_MAX_LENGTH)
    },
    toggleDescription(collectionId) {
      this.$set(this.expandedDescriptions, collectionId, !this.expandedDescriptions[collectionId])
    },
    onCollectionClick(collection) {
      if (window.self === window.top) return
      window.parent.postMessage({
        type: 'OPEN_COLLECTION',
        collectionId: collection._id,
        collectionName: collection.name,
      }, '*')
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-preview-collections {
  padding: var(--v2-space-4);

  &__tabs {
    display: flex;
    gap: var(--v2-space-1);
    border-bottom: 1px solid var(--v2-border);
    margin-bottom: var(--v2-space-4);
  }

  &__tab {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-2);
    padding: var(--v2-space-2) var(--v2-space-4);
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-muted-foreground);
    transition: color 0.15s ease, border-color 0.15s ease;

    &:hover {
      color: var(--v2-foreground);
    }

    &--active {
      color: var(--v2-primary);
      border-bottom-color: var(--v2-primary);
    }
  }

  &__tab-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 var(--v2-space-1);
    background: var(--v2-muted);
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, 200px);
    gap: var(--v2-space-4);
  }

  &__empty {
    text-align: center;
    padding: var(--v2-space-8) var(--v2-space-4);
  }
}

.v2-preview-card {
  max-width: 200px;
  // Solid card — Profile Preview modal expects a clean white substrate
  // (mesh substrate is hidden inside this iframe). Matches the "Add to
  // Order" modal cards which read as solid card surfaces inside their
  // V2Modal context.
  background: var(--v2-card);
  border: 1px solid var(--v2-border);
  border-radius: var(--v2-radius-lg);
  box-shadow: var(--v2-shadow-sm);
  cursor: pointer;
  contain: layout;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: var(--v2-primary);
    transform: translateY(-1px);
  }

  &__image {
    aspect-ratio: 4 / 3;
    overflow: hidden;
    background: var(--v2-muted);
    border-top-left-radius: var(--v2-radius-lg);
    border-top-right-radius: var(--v2-radius-lg);
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__body {
    padding: var(--v2-space-3) var(--v2-space-3);
  }

  &__name {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin-top: var(--v2-space-1);
  }

  &__description {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin-top: var(--v2-space-1);

    &--truncated {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &__read-more {
    font-size: 0.75rem;
    color: var(--v2-primary);
    cursor: pointer;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
}

.v2-text-muted {
  color: var(--v2-muted-foreground);
}
</style>

<!--
  Non-scoped override: the Profile Preview modal in PLG embeds this page
  via an iframe and expects a clean white substrate (no LayoutV2 mesh
  showing through behind the collection cards). We hide the v2-layout
  mesh and force a white body bg only when THIS page is rendered, using
  `:has(.v2-preview-collections)` so other v2 routes are unaffected.
-->
<style lang="scss">
body:has(.v2-preview-collections) {
  background: #ffffff;
}
body:has(.v2-preview-collections) .v2-layout::before {
  display: none;
}
</style>
